import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-general-bloodwork"),
  kind: "test",
  tags: ["lab", "her", "during-cycle", "testing", "ivf", "egg-freezing"],
  title: "Fertility Bloodwork",
  subtitle: "Routine blood draw to measure estradiol, LH, and progesterone",
  body: {
    testName: "Fertility Bloodwork (E2, LH, Progesterone)",
    category: "blood-marker",
    whatItMeasures:
      "Measures key reproductive hormones, most commonly estradiol (E2), luteinizing hormone (LH), and progesterone. These levels help your care team track ovarian response and time critical steps like trigger shots or transfers.",
    howItWorks:
      "A phlebotomist draws a small vial of blood from a vein in your arm, typically from the inside of the elbow. The sample is sent to the lab for hormone analysis, and results are usually available within a few hours. Your clinic may combine the blood draw with a monitoring ultrasound on the same visit to minimize trips. Estradiol reflects how actively your follicles are producing hormones, LH helps detect an impending ovulation surge, and progesterone confirms whether ovulation has occurred or supports luteal-phase monitoring. Arrive early in the morning if instructed; many clinics draw labs between 7-9 AM. Stay hydrated the night before and morning of to make the draw easier. No fasting is required unless your clinic specifies otherwise.",
    quickFacts: [
      { kind: "sample", value: "Blood" },
      { kind: "time", value: "5 min draw" },
      { kind: "pain", value: "Needle pinch" },
      { kind: "prep", value: "Morning draw if clinic asks" },
      { kind: "results", value: "Same day, often a few hours" },
    ],
    turnaroundTime: "Same day (a few hours)",
    costRange: { low: 50, high: 250 },
    atHome: false,
    clinicRequired: true,
    cycleDay:
      "Variable: day 2-4 for baseline E2/LH, repeated during monitoring, and about 7 days after ovulation for progesterone when confirming ovulation.",
    markerTiming: [
      {
        marker: "Estradiol (E2)",
        timing: "Day 2-4 for baseline; repeated during stimulation or FET prep",
        reason:
          "Baseline E2 shows whether the ovaries are quiet. During stimulation, rising E2 helps track follicle activity; each mature follicle often contributes roughly 200-300 pg/mL, but clinics interpret the trend with ultrasound findings.",
      },
      {
        marker: "LH",
        timing: "Day 2-4 for baseline; mid-cycle if checking for a surge",
        reason:
          "Baseline LH helps interpret brain-ovary signaling. A mid-cycle LH rise is a different use case because it signals possible ovulation.",
      },
      {
        marker: "Progesterone",
        timing:
          "About 7 days after ovulation for ovulation confirmation; protocol-specific during FET or stimulation",
        reason:
          "Progesterone rises after ovulation, so timing follows ovulation or the medication schedule rather than cycle day 2-4.",
      },
    ],
    dataPoints: [
      makeId("dp-estradiol-level"),
      makeId("dp-lh-level"),
      makeId("dp-progesterone-level"),
    ],
    section: "cycle-monitoring",
  },
  sources: [
    {
      title: "ESHRE guideline: ovarian stimulation for IVF/ICSI",
      authors: "ESHRE Guideline Group on Ovarian Stimulation",
      journal: "Human Reproduction Open",
      year: 2020,
      url: "https://academic.oup.com/hropen/article/2020/2/hoaa009/5826933",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 3,
};

registerBlock(test);
